import * as yup from 'yup'
import {
  ActivityType,
  DSAttendanceMonitoringType,
  DSAttendanceRuleRecurrence,
  EffectiveSourceKind,
  hasRulesAndTemplates
} from '@/constants/ds-attendance.constants'
import {
  DEFAULT_MANUAL_REVIEW_SAMPLE_RATE,
  DSAttendanceAutoApproval,
  DSAttendanceSheetConferenceBy
} from '@/constants/ds-attendance-auto-approval.constants'
import {
  LayerOperator,
  ALLOWED_VALIDATION_ROLES,
  MANDATORY_VALIDATION_ROLES,
  ROOT_LAYER_REQUIRES_AND,
  VALIDATION_ROLE_LABELS
} from '@/types/approval-flow.types'
import { toDateOnly } from '@/utils/date'

/**
 * Fontes que o ranking de precedência aceita. `MANUAL_LEGACY` e `LEGACY` ficam de fora:
 * são somente histórico e travados no fim da lista (Q4/Q16).
 */
const CONFIGURABLE_SOURCE_KINDS = Object.values(EffectiveSourceKind).filter(
  (kind) => kind !== EffectiveSourceKind.MANUAL_LEGACY && kind !== EffectiveSourceKind.LEGACY
)

/**
 * Prazo de uma cobrança automática (§1.5): inteiro, nunca negativo e com teto por
 * canal. `null` (default do produto) e ausente continuam válidos.
 */
const requestDelaySchema = (max: number, message: string) =>
  yup.number().nullable().typeError(message).integer(message).min(0, message).max(max, message)

/**
 * Q18 — janela de envio: `null` (padrão) ou 1..max. `0` é recusado: janela zero faz a
 * solicitação nascer vencida, que é exatamente o bug do incidente de 24/09.
 */
const sendWindowSchema = (max: number, message: string) =>
  yup
    .number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null || originalValue === undefined ? null : value
    )
    .typeError(message)
    .integer(message)
    .min(1, message)
    .max(max, message)

export const POLICY_DATE_MESSAGES = {
  endBeforeStart: 'Data de fim da vigência não pode ser anterior à data de início.',
  startAfterSchedule: 'Data de início da vigência não pode ser posterior ao fim da escala.',
  endAfterSchedule: 'Data de fim da vigência não pode ser posterior ao fim da escala.'
} as const

const maxScheduleEndDateTest = (message: string) =>
  yup.date().test('max-schedule-end-date', message, function (value) {
    const scheduleEndDate = this.options.context?.scheduleEndDate
    if (!value || !scheduleEndDate) return true
    return toDateOnly(value) <= toDateOnly(scheduleEndDate)
  })

export const step1Schema = yup.object().shape({
  scheduleName: yup.string().required('Nome da escala é obrigatório'),
  timezone: yup.string().nullable().required('Fuso horário é obrigatório'),
  endDate: yup.date().nullable(),
  healthUnitId: yup
    .number()
    .typeError('Unidade de saúde é obrigatória')
    .required('Unidade de saúde é obrigatória'),
  proportionality: yup.string().required('Regra de proporcionalidade obrigatória'),
  operationalGroup: yup.string().nullable().required('Grupo Operacional é obrigatório')
})

const itemSchema = yup.object().shape({
  title: yup.string().required('Título é obrigatório'),
  value: yup.mixed().nullable()
})

export const step2Schema = yup.object().shape({
  positions: yup.array().of(itemSchema).min(1, 'Adicione pelo menos uma posição'),
  shifts: yup.array().of(itemSchema).min(1, 'Adicione pelo menos um turno'),
  formations: yup.array().when('formationRequired', {
    is: true,
    then: (schema) => schema.of(itemSchema).min(1, 'Selecione pelo menos uma formação'),
    otherwise: (schema) => schema.of(itemSchema)
  }),
  formationRequired: yup.boolean().required()
})

export const step3Schema = yup.object().shape({
  name: yup.string().required('Nome da Política de Registro de Plantão é obrigatório'),
  startDate: yup
    .date()
    .nullable()
    .required('Data de início é obrigatória')
    .concat(maxScheduleEndDateTest(POLICY_DATE_MESSAGES.startAfterSchedule)),
  isIndeterminate: yup.boolean(),
  endDate: yup
    .date()
    .nullable()
    .when('isIndeterminate', {
      is: false,
      then: (schema) =>
        schema
          .required('Data de fim é obrigatória quando o prazo não for indeterminado')
          .min(yup.ref('startDate'), POLICY_DATE_MESSAGES.endBeforeStart)
          .concat(maxScheduleEndDateTest(POLICY_DATE_MESSAGES.endAfterSchedule)),
      otherwise: (schema) => schema.notRequired()
    }),
  monitoringType: yup.string().required('Modalidade de registro é obrigatória'),

  // Seção "Auditoria" (spec 2026-09-11, §6.1). Os dois segmentados nunca chegam vazios pela
  // tela; o `oneOf` guarda contra política carregada com valor desconhecido do backend.
  sheetConferenceBy: yup
    .string()
    .nullable()
    .oneOf(
      [...Object.values(DSAttendanceSheetConferenceBy), null],
      'Escolha quem confere a folha contra o espelho'
    ),
  autoApproval: yup
    .string()
    .nullable()
    .oneOf(
      [...Object.values(DSAttendanceAutoApproval), null],
      'Escolha o modo de aprovação automática'
    ),
  manualReviewSampleRate: yup
    .number()
    .nullable()
    /**
     * `v-model.number` num campo limpo devolve string vazia, que o yup casta para `NaN` e
     * reprova no `typeError`. Apagar o número significa "não sortear nada" = 0.
     */
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null || originalValue === undefined
        ? DEFAULT_MANUAL_REVIEW_SAMPLE_RATE
        : value
    )
    .typeError('Revisão humana por amostragem deve ser um número')
    .integer('Revisão humana por amostragem deve ser um número inteiro')
    .min(0, 'Revisão humana por amostragem deve ficar entre 0 e 100')
    .max(100, 'Revisão humana por amostragem deve ficar entre 0 e 100'),
  // F2 (spec 2026-09-12, §1.5) — Registro de Atividade e precedência das fontes.
  activityRecordsEnabled: yup.boolean(),
  // `null` = "usa o default do produto": é o que o formulário devolve enquanto o usuário
  // não mexe na lista de uma política que nunca a customizou.
  acceptedActivityTypes: yup
    .array()
    .of(yup.mixed<ActivityType>().oneOf(Object.values(ActivityType)))
    .nullable()
    .when('activityRecordsEnabled', {
      // Com o Registro de Atividade ligado e nenhum tipo aceito, o hub "Enviar registro"
      // não teria o que oferecer — o backend rejeita a mesma combinação (§1.5).
      is: true,
      then: (schema) => schema.min(1, 'Selecione ao menos um tipo'),
      otherwise: (schema) => schema.notRequired()
    }),
  /*
   * §8.4 — os campos das seções "Solicitação automática" (F3) e "Pós-plantão" (F4).
   * As SEÇÕES não entram na F2 (rollout por fase), mas os campos existem no schema
   * desde já: uma política que o backend já devolva com eles preenchidos não pode
   * ser reprovada pela validação nem perdê-los no round-trip do formulário.
   * `null` = default do produto; `0` = canal desligado (não é "vazio").
   */
  requestAfterCheckoutHoursApp: requestDelaySchema(
    720,
    'O prazo da solicitação no app deve ficar entre 0 e 720 horas'
  ),
  requestAfterHoursExternal: requestDelaySchema(
    720,
    'O prazo da solicitação no app externo deve ficar entre 0 e 720 horas'
  ),
  requestAfterDaysSheet: requestDelaySchema(
    30,
    'O prazo da solicitação por folha deve ficar entre 0 e 30 dias'
  ),
  activityRequestSendWindowHoursApp: sendWindowSchema(
    720,
    'O prazo para enviar pelo app deve ficar entre 1 e 720 horas'
  ),
  activityRequestSendWindowDaysSheet: sendWindowSchema(
    30,
    'O prazo para enviar a folha deve ficar entre 1 e 30 dias'
  ),
  postStationBlockOnRegistration: yup.boolean().nullable(),
  // `null` = ordem de fábrica ("não customizou", como em `acceptedActivityTypes`); uma
  // lista, quando vem, continua sujeita ao mínimo de 1 fonte e à ausência de repetição.
  sourcePrecedence: yup
    .array()
    .of(yup.mixed<EffectiveSourceKind>().oneOf(CONFIGURABLE_SOURCE_KINDS))
    .nullable()
    .min(1, 'A precedência das fontes precisa de ao menos uma fonte')
    // `MANUAL_LEGACY`/`LEGACY` são somente histórico: nunca podem ser escolhidos como
    // fonte de precedência, e repetir uma fonte tornaria o ranking ambíguo.
    .test(
      'sem-fontes-repetidas',
      'A precedência das fontes não pode repetir a mesma fonte',
      (value) => !value || new Set(value).size === value.length
    ),

  externalApp: yup
    .string()
    .nullable()
    .when('monitoringType', {
      is: DSAttendanceMonitoringType.EXTERNAL_APP,
      then: (schema) => schema.required('Informe o nome do aplicativo externo'),
      otherwise: (schema) => schema.notRequired().nullable()
    }),

  requiredCheckOurTeam: yup.boolean().when('monitoringType', {
    is: DSAttendanceMonitoringType.MINHA_ESCALA_APP,
    then: (schema) =>
      schema.oneOf([true, false]).required('Defina a obrigatoriedade para o time interno'),
    otherwise: (schema) => schema.notRequired()
  }),
  requiredCheckNotOurTeam: yup.boolean().when('monitoringType', {
    is: DSAttendanceMonitoringType.MINHA_ESCALA_APP,
    then: (schema) =>
      schema.oneOf([true, false]).required('Defina a obrigatoriedade para o time externo'),
    otherwise: (schema) => schema.notRequired()
  }),

  checkMethodNotOurTeam: yup
    .string()
    .nullable()
    .when('requiredCheckNotOurTeam', {
      is: true,
      then: (schema) => schema.required('Selecione o método de check-in para externos'),
      otherwise: (schema) => schema.notRequired().nullable()
    }),
  checkMethodOurTeam: yup
    .string()
    .nullable()
    .when('requiredCheckOurTeam', {
      is: true,
      then: (schema) => schema.required('Selecione o método de check-in para nosso time'),
      otherwise: (schema) => schema.notRequired().nullable()
    }),

  submissionDeadlineHours: yup
    .number()
    .nullable()
    .when('monitoringType', {
      is: (val: string) =>
        [
          DSAttendanceMonitoringType.FOLHA,
          DSAttendanceMonitoringType.EXTERNAL_APP,
          DSAttendanceMonitoringType.NON_DAILY_SHEET
        ].includes(val as DSAttendanceMonitoringType),
      then: (schema) =>
        schema
          .required('Limite de envio é obrigatório')
          .min(0, 'Não pode ser negativo')
          .typeError('Deve ser um número'),
      otherwise: (schema) => schema.notRequired()
    }),
  deadlineCalculationType: yup
    .string()
    .nullable()
    .when('monitoringType', {
      is: (val: string) =>
        [
          DSAttendanceMonitoringType.FOLHA,
          DSAttendanceMonitoringType.EXTERNAL_APP,
          DSAttendanceMonitoringType.NON_DAILY_SHEET
        ].includes(val as DSAttendanceMonitoringType),
      then: (schema) => schema.required('Cálculo de horas é obrigatório'),
      otherwise: (schema) => schema.notRequired()
    }),

  checkinLateToleranceMinutes: yup
    .number()
    .nullable()
    .when('monitoringType', {
      is: (val: string) =>
        [
          DSAttendanceMonitoringType.EXTERNAL_APP,
          DSAttendanceMonitoringType.NON_DAILY_SHEET
        ].includes(val as DSAttendanceMonitoringType),
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema
          .required('Tolerância de atraso é obrigatória')
          .min(0, 'Não pode ser negativo')
          .typeError('Deve ser um número válido')
    }),

  checkoutEarlyToleranceMinutes: yup
    .number()
    .nullable()
    .when('monitoringType', {
      is: (val: string) =>
        [
          DSAttendanceMonitoringType.EXTERNAL_APP,
          DSAttendanceMonitoringType.NON_DAILY_SHEET
        ].includes(val as DSAttendanceMonitoringType),
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema
          .required('Tolerância de saída é obrigatória')
          .min(0, 'Não pode ser negativo')
          .typeError('Deve ser um número válido')
    }),

  rules: yup.array().when('monitoringType', {
    is: DSAttendanceMonitoringType.FOLHA,
    then: (schema) =>
      schema
        .of(
          yup.object().shape({
            name: yup
              .string()
              .required('Nome da folha é obrigatório')
              .max(100, 'Máximo de 100 caracteres'),
            shiftIds: yup
              .array()
              .of(yup.mixed().required())
              .min(1, 'Selecione pelo menos um turno')
              .required('Turno obrigatório'),
            recurrence: yup.string().nullable().required('Recorrência obrigatória'),

            dayOfMonth: yup
              .number()
              .nullable()
              .when('recurrence', {
                is: (val: string) => val === DSAttendanceRuleRecurrence.MONTHLY,
                then: (schema) =>
                  schema.required('Dia obrigatório').min(1, 'Mínimo 1').max(31, 'Máximo 31'),
                otherwise: (schema) => schema.nullable().notRequired()
              }),

            daysOfWeek: yup.array().min(1, 'Selecione pelo menos um dia da semana')
          })
        )
        .min(1, 'Adicione pelo menos uma folha de monitoramento'),
    otherwise: (schema) => schema.notRequired()
  }),
  templates: yup.array().when('monitoringType', {
    is: (val: string) => hasRulesAndTemplates(val),
    then: (schema) => schema.min(1, 'Adicione pelo menos um modelo de folha'),
    otherwise: (schema) => schema.notRequired()
  })
})

export const stepExceptionalidadeSchema = yup.object({
  layers: yup
    .array(
      yup.object({
        operator: yup
          .mixed<LayerOperator>()
          .oneOf(Object.values(LayerOperator))
          .default(LayerOperator.AND),
        roles: yup
          .array(
            yup.object({
              role: yup
                .mixed()
                .oneOf(ALLOWED_VALIDATION_ROLES)
                .required('Papel de validação é obrigatório')
            })
          )
          .when('operator', {
            is: LayerOperator.OR,
            then: (schema) =>
              schema.min(2, 'O tipo "Qualquer um - OU" requer pelo menos 2 papéis validadores'),
            otherwise: (schema) => schema.min(1, 'Cada camada deve ter ao menos um papel validador')
          })
          .required()
      })
    )
    .min(1, 'O fluxo de aprovação deve ter ao menos uma camada')
    .required('Configuração de camadas é obrigatória')
    // A primeira camada é a raiz do fluxo (`groupToApiLayers` a emite primeiro).
    // Ela carrega os papéis obrigatórios, em `AND`: sob `OR` bastaria um deles
    // assinar e a exigência conjunta deixaria de valer.
    .test(
      'raiz-com-papeis-obrigatorios',
      ({ value }) => {
        const present = new Set((value?.[0]?.roles ?? []).map((r: { role: string }) => r.role))
        const missing = MANDATORY_VALIDATION_ROLES.filter((role) => !present.has(role))
        return `A primeira camada precisa conter ${missing
          .map((role) => VALIDATION_ROLE_LABELS[role])
          .join(' e ')}.`
      },
      (layers) => {
        const rootRoles = new Set((layers?.[0]?.roles ?? []).map((r) => r?.role))
        return MANDATORY_VALIDATION_ROLES.every((role) => rootRoles.has(role))
      }
    )
    // Só se aplica com `ROOT_LAYER_REQUIRES_AND` ligada — desligada, a raiz
    // aceita "Todos - E" ou "Qualquer um - OU" como qualquer outra camada.
    .test(
      'raiz-com-operador-e',
      'A primeira camada precisa usar o tipo de validação "Todos - E".',
      (layers) =>
        !ROOT_LAYER_REQUIRES_AND || (layers?.[0] ? layers[0].operator === LayerOperator.AND : true)
    )
})

export const step4Schema = yup.object().shape({
  name: yup.string().required('Informe o nome da política de pagamento.'),
  startDate: yup
    .date()
    .nullable()
    .required('Informe a data de início da vigência.')
    .concat(maxScheduleEndDateTest(POLICY_DATE_MESSAGES.startAfterSchedule)),
  indefiniteEndDate: yup.boolean(),
  endDate: yup
    .date()
    .nullable()
    .when('indefiniteEndDate', {
      is: false,
      then: (schema) =>
        schema
          .required('Informe a data de fim da vigência.')
          .min(yup.ref('startDate'), POLICY_DATE_MESSAGES.endBeforeStart)
          .concat(maxScheduleEndDateTest(POLICY_DATE_MESSAGES.endAfterSchedule)),
      otherwise: (schema) => schema.notRequired()
    }),
  rules: yup
    .array()
    .of(
      yup.object().shape({
        days: yup.array().min(1, 'Selecione os dias.'),
        shifts: yup.array().min(1, 'Selecione os turnos.'),
        positions: yup.array().min(1, 'Selecione as posições.'),
        teams: yup.array().min(1, 'Selecione os times.'),
        repassMode: yup.string().required('Selecione o modelo de repasse.'),
        dutyValue: yup.string().required('Informe o valor.')
      })
    )
    .min(1, 'Adicione pelo menos uma regra de pagamento.')
})

/**
 * Schema apenas de vigencia, derivado de step4Schema — fonte unica das
 * regras de datas do vinculo entre escala e politica de pagamento.
 * Reutilizado pelo modal de politica de pagamento na edicao de escala;
 * requer o mesmo `context.scheduleEndDate` de step4Schema.
 */
export const vigencySchema = step4Schema.pick(['startDate', 'endDate', 'indefiniteEndDate'])
